import { useState, useRef, useEffect } from 'react'

export type PaisOpcao = {
  codigo: string
  nome: string
  bandeira: string
  placeholder: string
  nomeSalvo: string
  /** Quantidade exata de dígitos exigida (Brasil/Portugal/EUA). undefined = livre dentro de min/max */
  digitosExatos?: number
  digitosMin: number
  digitosMax: number
  exemplo: string
  mensagemErro: string
}

export const PAISES: PaisOpcao[] = [
  {
    codigo: '+55',
    nome: 'Brasil',
    bandeira: '🇧🇷',
    placeholder: '(DDD) 9 9999-9999',
    nomeSalvo: 'Brasil',
    digitosExatos: 11,
    digitosMin: 11,
    digitosMax: 11,
    exemplo: '81999999999',
    mensagemErro: 'Digite DDD + 9 dígitos. Exemplo: 81999999999',
  },
  {
    codigo: '+351',
    nome: 'Portugal',
    bandeira: '🇵🇹',
    placeholder: '9XX XXX XXX',
    nomeSalvo: 'Portugal',
    digitosExatos: 9,
    digitosMin: 9,
    digitosMax: 9,
    exemplo: '912345678',
    mensagemErro: 'Digite 9 dígitos. Exemplo: 912345678',
  },
  {
    codigo: '+1',
    nome: 'Estados Unidos',
    bandeira: '🇺🇸',
    placeholder: '(XXX) XXX-XXXX',
    nomeSalvo: 'Estados Unidos',
    digitosExatos: 10,
    digitosMin: 10,
    digitosMax: 10,
    exemplo: '2125551234',
    mensagemErro: 'Digite 10 dígitos. Exemplo: 2125551234',
  },
  {
    codigo: '',
    nome: 'Outro',
    bandeira: '🌍',
    placeholder: 'Número com código',
    nomeSalvo: 'Outro',
    digitosMin: 7,
    digitosMax: 15,
    exemplo: '',
    mensagemErro: 'Digite um número válido',
  },
]

/**
 * Valida o número conforme regras do país.
 * Retorna { valido: boolean, mensagem: string }.
 * Use no submit dos formulários antes de salvar no banco.
 */
export function validarNumero(value: string, pais: PaisOpcao): { valido: boolean; mensagem: string } {
  const digitos = (value || '').replace(/\D/g, '')
  if (digitos.length === 0) {
    return { valido: false, mensagem: pais.mensagemErro }
  }
  if (typeof pais.digitosExatos === 'number') {
    if (digitos.length !== pais.digitosExatos) {
      return { valido: false, mensagem: pais.mensagemErro }
    }
  } else {
    if (digitos.length < pais.digitosMin || digitos.length > pais.digitosMax) {
      return { valido: false, mensagem: pais.mensagemErro }
    }
  }
  return { valido: true, mensagem: '' }
}

interface PhoneInputProps {
  value: string
  onChange: (numero: string, pais: PaisOpcao) => void
  required?: boolean
  disabled?: boolean
  /** País inicial opcional (usado ao pré-carregar um número já salvo). */
  paisInicial?: PaisOpcao
}

export default function PhoneInput({ value, onChange, required, disabled, paisInicial }: PhoneInputProps) {
  const [paisSelecionado, setPaisSelecionado] = useState<PaisOpcao>(paisInicial || PAISES[0])
  const [dropdownAberto, setDropdownAberto] = useState(false)
  const [tocado, setTocado] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownAberto(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const selecionarPais = (pais: PaisOpcao) => {
    setPaisSelecionado(pais)
    setDropdownAberto(false)
    setTocado(false)
    // Limpar campo ao trocar país (regra obrigatória)
    onChange('', pais)
  }

  const handleNumeroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Bloquear letras/caracteres especiais durante digitação — manter apenas dígitos
    let apenasDigitos = e.target.value.replace(/\D/g, '')
    // Aplicar limite máximo dinâmico conforme país
    const limite = paisSelecionado.digitosMax
    if (apenasDigitos.length > limite) {
      apenasDigitos = apenasDigitos.slice(0, limite)
    }
    if (!tocado) setTocado(true)
    onChange(apenasDigitos, paisSelecionado)
  }

  const digitosAtuais = (value || '').replace(/\D/g, '').length
  const totalEsperado = paisSelecionado.digitosExatos ?? paisSelecionado.digitosMax
  const completo =
    typeof paisSelecionado.digitosExatos === 'number'
      ? digitosAtuais === paisSelecionado.digitosExatos
      : digitosAtuais >= paisSelecionado.digitosMin && digitosAtuais <= paisSelecionado.digitosMax

  const mostrarErro = tocado && digitosAtuais > 0 && !completo

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="flex w-full">
        {/* Seletor de bandeira */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setDropdownAberto(!dropdownAberto)}
          className={`
            flex items-center gap-1.5 px-3 py-3 border-2 border-r-0 rounded-l-xl
            bg-white transition-all select-none whitespace-nowrap
            ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#F0FAF4]'}
            ${dropdownAberto ? 'border-[#52B788]' : 'border-[#EDE0D4]'}
          `}
          style={{ minWidth: '100px' }}
        >
          <span className="text-xl leading-none">{paisSelecionado.bandeira}</span>
          <span className="text-sm font-medium text-[#2C2C2A]">{paisSelecionado.codigo || '+'}</span>
          <svg
            className={`w-3 h-3 text-[#5F5E5A] transition-transform ${dropdownAberto ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Campo de número */}
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="tel-national"
          value={value}
          onChange={handleNumeroChange}
          onBlur={() => setTocado(true)}
          required={required}
          disabled={disabled}
          maxLength={paisSelecionado.digitosMax}
          placeholder={paisSelecionado.placeholder}
          className={`
            flex-1 px-4 py-3 border-2 border-l-0 rounded-r-xl focus:outline-none transition-all
            ${disabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300'
              : mostrarErro
                ? 'border-[#F87171] focus:border-[#EF4444] bg-white'
                : 'border-[#EDE0D4] focus:border-[#52B788] bg-white'
            }
          `}
        />

        {/* Dropdown */}
        {dropdownAberto && (
          <div className="absolute top-full left-0 z-50 mt-1 w-64 bg-white border-2 border-[#EDE0D4] rounded-xl shadow-lg overflow-hidden">
            {PAISES.map(pais => (
              <button
                key={pais.nomeSalvo}
                type="button"
                onClick={() => selecionarPais(pais)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                  ${paisSelecionado.nomeSalvo === pais.nomeSalvo
                    ? 'bg-[#D8F3DC] text-[#2D6A4F]'
                    : 'hover:bg-[#F8F4EF] text-[#2C2C2A]'}
                `}
              >
                <span className="text-xl">{pais.bandeira}</span>
                <span className="flex-1 text-sm font-medium">{pais.nome}</span>
                {pais.codigo && (
                  <span className="text-sm text-[#5F5E5A]">{pais.codigo}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Contador + mensagem de erro */}
      <div className="flex items-center justify-between mt-1.5 px-1 text-xs">
        <span
          className={`
            ${mostrarErro
              ? 'text-[#991B1B]'
              : completo && digitosAtuais > 0
                ? 'text-[#2D6A4F] font-medium'
                : 'text-[#5F5E5A]'}
          `}
        >
          {mostrarErro
            ? paisSelecionado.mensagemErro
            : paisSelecionado.nomeSalvo === 'Outro'
              ? `${digitosAtuais} dígito${digitosAtuais === 1 ? '' : 's'} (mín. ${paisSelecionado.digitosMin}, máx. ${paisSelecionado.digitosMax})`
              : `${digitosAtuais}/${totalEsperado} dígitos`}
        </span>
        {completo && digitosAtuais > 0 && !mostrarErro && (
          <span className="text-[#2D6A4F] font-medium">✓</span>
        )}
      </div>
    </div>
  )
}
